/*
 * Copyright (c) 2020 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import React from 'react';
import {Header} from "@scuf/common";
import './wizard/GatewayRegistration/SiteSelection.scss'
import {PureComponent} from "react";
import {
  GatewayPropertiesValidationStatus,
  IDIYGatewayManagementProps,
  IDIYGatewayManagementState,
  IRegistrationDetails,
  ISiteDetails,
} from "../stores/GatewayManager/IGatewayRegistrationInterfaces";
import {inject, observer} from "mobx-react";
import {SiteSelectionState} from "./state/SiteSelectionState";
import {ContactSupportState} from "./state/ContactSupportState";
import {AbstractWizardState} from "./state/AbstractWizardState";
import {DIYErrorPage} from "./common/DIYErrorPage";
import featureFlagStore from '../utils/FeatureFlags/APFeatureFlagsStore';
import {observe} from "mobx";

/**
 * This page is the wrapper for all the other pages in the DIY app.
 * It encapsulates the authentication mechanism using ADAL.
 *
 * Once the token is acquired, the individual wizard screens are loaded
 */

@inject("globalStore", "gatewayValidationStore")
@observer
export default class DIYGatewayManagement extends PureComponent<IDIYGatewayManagementProps, IDIYGatewayManagementState> {

  /**
   * Initialize the state of the application
   * Acquire the authentication context.
   */
  constructor(props: IDIYGatewayManagementProps) {
    super(props);
    this.initializeState();
  }

  /**
   * Capture the query parameters from the window URL and validate them
   *
   */
  componentDidMount() {
    let queryParameters = this.getQueryParameters();
    this.setState({
      ...queryParameters,
      validationStatus: this.gatewayParametersValid(queryParameters)
    });
    observe(featureFlagStore.userContextInitialized, () => {
      this.setState({
        featureFlagUserContextInitialized: true
      });
    });
    featureFlagStore.identify(this.props.userInfo!.userPrincipalName);
  }

  /**
   * Render the site/location selection for associating the gateway
   */
  render() {
    let registrationStatus = this.state.registrationStatus;
    return <div className='root'>
      <div className='application-header'>
        <Header title="Connect" logo={false} isForge={true} menu={false}
                className='ui-application-header'>
          <Header.UserProfile firstName={this.state.firstName}
                              lastName={this.state.lastName}>
            <Header.UserProfile.Item onClick={() => {
              this.props.logout!();
            }}>Log out</Header.UserProfile.Item>
            <Header.UserProfile.Item onClick={() => {
              window.open("/license.txt")
            }}>License</Header.UserProfile.Item>
          </Header.UserProfile>
        </Header>
      </div>
      <div className='application-container'>
      <div className='application-main-content'>
        {// @Todo Instead of relying on state transitions to get content,
          // use the react ROUTER5. Needs a little study though
          (this.state.featureFlagUserContextInitialized) ?
          (this.state.validationStatus === GatewayPropertiesValidationStatus.SUCCESS
          || (this.state.registrationStatus instanceof ContactSupportState)) ?
            registrationStatus.getContent() :
            <DIYErrorPage errorTitle='Invalid registration parameters'
                          message={this.getErrorMessage()} showSupportLink={true}
                          supportHandler = {
                            () =>  this.launchContactSupport(this.state.registrationStatus)}/>: <></>
        }
        </div>
      </div>
    </div>;
  }

  /**
   * Launch the contact support screen
   */
  launchContactSupport = (currentState: AbstractWizardState) => {
    this.setState({
      registrationStatus: new ContactSupportState(this, currentState)
    });
  };

  /**
   * Return the query parameter values from window.location.query
   * @returns {IRegistrationDetails}
   */
  private getQueryParameters = (): IRegistrationDetails => {
    const queryString = require('query-string');
    const parsedQueryString = queryString.parse(window.location.search);
    let sn = parsedQueryString.deviceSerialNumber,
      epName = parsedQueryString.originalEp,
      devId = parsedQueryString.deviceId,
      enrId = parsedQueryString.enrollmentId;
    return {
      serialNumber: sn,
      epName: epName,
      deviceId: devId,
      enrollmentId: enrId
    };
  };

  /**
   * Check if the gateway parameters are valid
   * @returns {boolean}
   * The rules of validation are :-
   *  Serial number is mandatory
   *  Original end Point name is mandatory
   *  If pre registered snaps are allowed and device id is available, it should be in valid format
   *  If enrollment Id exists, device id cannot be present (For teh first-to-claim case)
   */
  private gatewayParametersValid = (regProperties: IRegistrationDetails) => {
    let envVars = this.props.globalStore!.envVariables;
    if (!regProperties.serialNumber) return GatewayPropertiesValidationStatus.INVALID_SERIAL_NUMBER;
    if (!regProperties.epName) return GatewayPropertiesValidationStatus.INVALID_ENDPOINT_NAME;
    if (!!regProperties.enrollmentId) {
      // In the first-to-claim snaps, the enrollment identity is mandatory
      return (/^A-[A-Za-z0-9:]{95}$/.test(regProperties.enrollmentId)) ? GatewayPropertiesValidationStatus.SUCCESS :
        GatewayPropertiesValidationStatus.INVALID_ENROLLMENT_ID;
    } else {
      if (!!regProperties.deviceId) {
        // Check if pre registration is enabled
        // This will be turned to false for production
        if (envVars.ALLOW_PRE_REGISTERED_SNAPS !== 'True') {
          return GatewayPropertiesValidationStatus.PRE_REG_GATEWAYS_NOT_ALLOWED;
        }
        else {
          return (/^[A-Za-z0-9]{32}/.test(regProperties.deviceId)) ? GatewayPropertiesValidationStatus.SUCCESS :
            GatewayPropertiesValidationStatus.INVALID_DEVICE_ID
        }
      }
    }
    return GatewayPropertiesValidationStatus.UNKNOWN;
  };

  /**
   * Get the error message based on status
   * @returns {string}
   */
  private getErrorMessage() {
    let validationStatus = this.state.validationStatus;
    if (validationStatus === GatewayPropertiesValidationStatus.INVALID_SERIAL_NUMBER) {
      return "Serial number for the gateway is not available.";
    }
    if (validationStatus === GatewayPropertiesValidationStatus.INVALID_ENDPOINT_NAME) {
      return "End point name for the gateway is not available.";
    }
    if (validationStatus === GatewayPropertiesValidationStatus.INVALID_ENROLLMENT_ID
    || validationStatus === GatewayPropertiesValidationStatus.UNKNOWN) {
      return "Enrollment id is mandatory and needs to be in valid format for registering the gateway.";
    }
    if (validationStatus === GatewayPropertiesValidationStatus.INVALID_DEVICE_ID) {
      return "Device id is mandatory and needs to be in valid format for associating a registered gateway to a site.";
    }
    if (validationStatus === GatewayPropertiesValidationStatus.PRE_REG_GATEWAYS_NOT_ALLOWED) {
      return "The ability to associate a pre registered gateway with a site is not enabled.";
    }
    return "Insufficient/Invalid data for registration.";
  }

  /**
   * Initialize the state of the application.
   * Set the starting page to SiteSelection.
   */
  private initializeState() {
    let userInfo = this.props.userInfo!;
    this.state = {
      validationStatus: GatewayPropertiesValidationStatus.UNKNOWN,
      featureFlagUserContextInitialized: false,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      serialNumber: '',
      epName: '',
      selectedSite: {
        siteId: '',
        siteName: '',
        customerDetails: {}
      } as ISiteDetails,
      registrationStatus: new SiteSelectionState(this)
    };
  }

}
